import { Sprite } from 'pixi.js-legacy';

interface TileAnimation {
  sprite: Sprite;
  startX: number;
  startY: number;
  targetX: number;
  targetY: number;
  progress: number;
}

/**
 * Manages smooth tile movement animations (gravity/compaction)
 */
export class TileAnimator {
  private animations: TileAnimation[] = [];
  private isAnimating: boolean = false;
  private animationSpeed: number = 0.15; // Higher = faster

  /**
   * Start animating a tile from current to target position
   */
  animateTile(
    sprite: Sprite,
    startX: number,
    startY: number,
    targetX: number,
    targetY: number
  ): void {
    // Only animate if there's actual movement
    if (startX === targetX && startY === targetY) {
      return;
    }

    this.animations.push({
      sprite,
      startX,
      startY,
      targetX,
      targetY,
      progress: 0,
    });

    this.isAnimating = true;
  }

  /**
   * Update all active tile animations
   */
  update(deltaTime: number): void {
    if (!this.isAnimating) return;

    for (let i = this.animations.length - 1; i >= 0; i--) {
      const anim = this.animations[i];

      // Update progress
      anim.progress += this.animationSpeed * deltaTime;

      if (anim.progress >= 1) {
        // Animation complete
        anim.sprite.x = anim.targetX;
        anim.sprite.y = anim.targetY;
        this.animations.splice(i, 1);
      } else {
        // Ease out cubic for smooth deceleration
        const t = anim.progress;
        const eased = 1 - Math.pow(1 - t, 3);

        // Interpolate position
        anim.sprite.x = anim.startX + (anim.targetX - anim.startX) * eased;
        anim.sprite.y = anim.startY + (anim.targetY - anim.startY) * eased;
      }
    }

    // Check if all animations are complete
    if (this.animations.length === 0) {
      this.isAnimating = false;
    }
  }

  /**
   * Check if any animations are running
   */
  isRunning(): boolean {
    return this.isAnimating;
  }

  /**
   * Clear all active animations
   */
  clear(): void {
    this.animations = [];
    this.isAnimating = false;
  }
}
